// Texterna för katalogen i kontot: prissättning, pris och vilka den gäller,
// som i designen Admin-Katalog. Belopp i öre.
import type { ServiceCatalog } from './sammanfattning.js';
import { kr } from './sammanfattning.js';

export type Service = ServiceCatalog['services'][number];
export type Pricing = Service['pricing'];
export type PricingType = Pricing['type'];
export type Billing = Service['billing'];
export type LegalForm = 'AB' | 'EF' | 'HB' | 'KB' | 'EK' | 'BRF' | 'IF';

export const BOLAGSFORMER: { kod: LegalForm; namn: string }[] = [
  { kod: 'AB', namn: 'Aktiebolag' },
  { kod: 'EF', namn: 'Enskild firma' },
  { kod: 'HB', namn: 'Handelsbolag' },
  { kod: 'KB', namn: 'Kommanditbolag' },
  { kod: 'EK', namn: 'Ekonomisk förening' },
  { kod: 'BRF', namn: 'Bostadsrättsförening' },
  { kod: 'IF', namn: 'Ideell förening' },
];

export const PERIODER: { kod: Billing; namn: string; grupp: string }[] = [
  { kod: 'monthly', namn: 'Per månad', grupp: 'Per månad' },
  { kod: 'yearly', namn: 'Per år', grupp: 'Per år' },
  { kod: 'once', namn: 'En gång', grupp: 'En gång' },
];

export const PRISSATTNINGAR: { typ: PricingType; namn: string; beskrivning: string }[] = [
  { typ: 'fixed', namn: 'Fast pris', beskrivning: 'Samma pris för alla' },
  { typ: 'perUnit', namn: 'Per enhet', beskrivning: 'T.ex. per anställd' },
  { typ: 'tiered', namn: 'Trappa', beskrivning: 'Pris i nivåer efter volym' },
  {
    typ: 'byLegalForm',
    namn: 'Per bolagsform',
    beskrivning: 'Olika för AB, EF, HB och föreningar',
  },
  { typ: 'hourly', namn: 'Timpris', beskrivning: 'Uppskattad tid × ert timpris' },
];

/** Drivarna som AI känner till, med svenska namn. Andra visas med sitt namn. */
export const DRIVARE: { kod: string; namn: string; kort: string; enhet: string }[] = [
  {
    kod: 'transactionsPerMonth',
    namn: 'Transaktioner per månad',
    kort: 'transaktioner/mån',
    enhet: 'transaktion',
  },
  { kod: 'employees', namn: 'Anställda med lön', kort: 'anställda', enhet: 'anställd' },
];

export const drivare = (kod: string) =>
  DRIVARE.find((d) => d.kod === kod) ?? { kod, namn: kod, kort: kod, enhet: kod };

/** "Trappa · transaktioner/mån", "Per anställd", "Fast". */
export function prissattningText(p: Pricing): string {
  switch (p.type) {
    case 'fixed':
      return 'Fast';
    case 'perUnit':
      return `Per ${p.unit}`;
    case 'tiered':
      return `Trappa · ${drivare(p.driver).kort}`;
    case 'byLegalForm':
      return 'Per bolagsform';
    case 'hourly':
      return 'Timpris';
  }
}

function spann(belopp: number[]): string {
  const min = Math.min(...belopp);
  const max = Math.max(...belopp);
  return min === max ? kr(min) : `${kr(min).replace(' kr', '')}–${kr(max)}`;
}

/** "1 500–4 900 kr", "300 kr", "1 100 kr/tim". Belopp exkl. moms. */
export function prisText(p: Pricing): string {
  switch (p.type) {
    case 'fixed':
    case 'perUnit':
      return kr(p.price);
    case 'tiered':
      return spann(p.tiers.map((t) => t.price));
    case 'byLegalForm':
      return spann(Object.values(p.prices).filter((v): v is number => v !== undefined));
    case 'hourly':
      return `${kr(p.rate)}/tim`;
  }
}

/** "Alla", "Aktiebolag" eller "AB, HB". */
export function gallerText(s: Service): string {
  if (!s.appliesTo || s.appliesTo.length === BOLAGSFORMER.length) return 'Alla';
  if (s.appliesTo.length === 1) {
    return BOLAGSFORMER.find((b) => b.kod === s.appliesTo?.[0])?.namn ?? s.appliesTo[0] ?? '';
  }
  return s.appliesTo.join(', ');
}
